//Vite no longer globally exposes jQuery, so we need to import it manually for embedSVG function
import $ from 'jquery';
window.$ = window.jQuery = $;

import 'lib/Init';
import { createApp } from 'vue';
import VueExtensions from 'lib/VueExtensions';
import Cookies from 'js-cookie';
import { axios } from 'lib/Init';
import Swal from 'lib/SwalExtension';
import { __, navigateToUrl } from 'lib/Utilities';
import PurchaseFlowMixin from 'lib/mixins/PurchaseFlowMixin';
import LicenseQuantity from 'components/smb/LicenseQuantity.vue';
import BusinessDetails from 'components/smb/BusinessDetails.vue';
import BusinessUrl from 'components/smb/BusinessUrl.vue';
import BillingDetails from 'components/smb/BillingDetails.vue';
import Terms from 'components/smb/Terms.vue';
import CardInformation from 'components/smb/CardInformation.vue';
import DetailVerification from 'components/smb/DetailVerification.vue';
import Invoice from 'components/smb/Invoice.vue';
import Contact from 'components/smb/Contact.vue';
import Notification from 'components/Notification.vue';

// Since components now use vee-validate 4 internally, we don't need global validation setup

declare const gtag: any;

const INDICATOR_MINIMUM_OPACITY = '0.05';
const INDICATOR_DISTANCE_OPACITY = [
    '1.00',
    '0.35',
    '0.25',
    '0.15',
    '0.10',
    INDICATOR_MINIMUM_OPACITY
];

const purchaseFlow = [
    { component: 'LicenseQuantity', trackingName: 'NumberOfEmployees' },
    { component: 'BusinessDetails', trackingName: 'BusinessName' },
    { component: 'BusinessUrl', trackingName: 'ProgramURL' },
    { component: 'Terms', trackingName: 'TermsAndConditions' },
];

declare function embedSVG();

const app = createApp({
    mixins: [PurchaseFlowMixin],
    data() {
        return {
            numLicenses: "",
            freeLicenses: 0,
            businessDetails: {},
            url: "",
            creditcardReferrals: [],
            invoice: {
                taxPercentage: 0,
                referralName: '',
                referral: {},
                promotionCode: '',
                discountPercentage: 0,
                promotionCodeID: null,
                invoiceAmount: 0,
            },
            showContactFlow: false,
            maxLicenseQuantity: window.s.maxLicenseQuantity,
            purchaseComponents: purchaseFlow,
            taxDetails: [],
            hideInvoice: window.s.hideInvoice,
            notification: {
                title: "",
                message: "",
                status: "success",
                show: false
            },
        };
    },
    created(){
        this.getCookies();
        const promotionCodeData = window.s.promotionCode;
        this.setPurchaseFlow(promotionCodeData);
        if(promotionCodeData){
            this.invoice.referralName = promotionCodeData.referralName;
            this.invoice.referral = promotionCodeData.referral;
            this.invoice.promotionCode = promotionCodeData.code;
            this.invoice.discountPercentage = promotionCodeData.discountRate * 100;
            this.creditcardReferrals = promotionCodeData.creditcardReferrals;
            this.freeLicenses = promotionCodeData.freeLicenses;
            this.invoice.promotionCodeID = promotionCodeData.promotionCodeID;
        }
    },
    mounted() {
        embedSVG();
        this.updatePurchaseFlowIndicator();
        this.sendPageViewHit(this.currentComponentIndex + 1, purchaseFlow[this.currentComponentIndex].trackingName);
    },
    methods: {
        showNotification(options) {
            this.notification.title = options.title;
            this.notification.message = options.message || "";
            this.notification.status = options.status || 'success';
            this.notification.show = true;
            
            // Auto-hide after 8 seconds unless sticky
            if (!options.sticky) {
                setTimeout(() => {
                    this.hideNotification();
                }, 8000);
            }
        },
        hideNotification() {
            this.notification.show = false;
        },
        setPurchaseFlow(promotionCodeData){
            if(promotionCodeData && promotionCodeData.code === "ADP100"){
                purchaseFlow.push({ component: 'DetailVerification', trackingName: 'DetailVerification' });
            } else {
                // Please don't change the splice Index as it will change order of pages
                // in SMB Purchase Flow
                purchaseFlow.splice(3, 0, { component: 'BillingDetails', trackingName: 'BillingInformation' });
                purchaseFlow.push({ component: 'CardInformation', trackingName: 'Payment' });
            }
        },
        componentWillAnimate() {
            this.toggleButtons(true);
        },
        componentDidAnimate() {
            this.updatePurchaseFlowIndicator(this.currentComponentIndex);
            this.toggleButtons(false);
        },
        getImageLink(imageFileName) {
            return `${window.s.assetPath}/${imageFileName}`;
        },
        nextComponent() {
            if(this.currentComponentIndex >= purchaseFlow.length) {
                return ;
            }
            this.setLastComponentIndex();
            this.componentWillAnimate();
            this.currentComponentIndex++;
            if(window.v._componentAnimationTimer) {
                clearTimeout(window.v._componentAnimationTimer);
            }
            window.v._componentAnimationTimer = setTimeout( () => {
                this.componentDidAnimate();
            }, 500);

            this.sendPageViewHit(this.currentComponentIndex + 1, purchaseFlow[this.currentComponentIndex].trackingName);
            this.setCookies();
        },
        nextButtonDidClick(e) {
            this.$refs.currentComponentRef.formWillSubmit(e);
        },
        toggleButtons(hide = true) {
            embedSVG();
            if(hide) {
                if(this.$refs.nextButton) {
                    this.$refs.nextButton.classList.add('purchase-buttons-enter');
                }

                if(this.$refs.backButton) {
                    this.$refs.backButton.classList.add('purchase-buttons-enter');
                }
            } else {
                if(this.$refs.nextButton) {
                    this.$refs.nextButton.classList.remove('purchase-buttons-enter');
                }

                if(this.$refs.backButton) {
                    this.$refs.backButton.classList.remove('purchase-buttons-enter');
                }
            }

        },
        backButtonDidClick() {
            if(this.showContactFlow === true){
                this.currentComponentIndex = 0;
                this.lastComponentIndex = 0;this.showContactFlow = false;
            } else {
                this.previousComponent();
            }
        },
        getCookies() {
            if(Cookies.get('venngo_cookies')){
                const venngoCookies = JSON.parse(Cookies.get('venngo_cookies'));
                if(venngoCookies.smbCookies){
                    const smbCookies = venngoCookies.smbCookies;
                    this.numLicenses = smbCookies.numLicenses;
                    this.freeLicenses = smbCookies.freeLicenses;
                    this.businessDetails = smbCookies.businessDetails;
                    this.url = smbCookies.url;
                    this.billingDetails = smbCookies.billingDetails;
                    this.invoice = smbCookies.invoice;

                    // Make sure you get the correct referral Name from Cookies
                    this.invoice.referralName = window.l.locale === "en" ? smbCookies.invoice.referral.name : smbCookies.invoice.referral.name_fr;
                    this.lastComponentIndex = smbCookies.lastComponentIndex;
                    this.currentComponentIndex = smbCookies.currentComponentIndex;
                    this.creditcardReferrals = smbCookies.creditcardReferrals;
                }
            }
        },
        removePaymentCookies() {
            Object.keys(Cookies.get()).forEach((cookie) => {
                Cookies.remove(cookie);
            });
        },
        contactWillShow() {
            this.showContactFlow = true;
        },
        businessDetailsDidSubmit(businessDetails) {
            this.businessDetails = businessDetails;
            this.nextComponent();
        },
        urlDidSubmit(url) {
            axios.post(window.s.API.urlCheck, {
                url
            }).then(response => {
                const responseData = response.data;
                if(responseData.success){
                    this.url = url;
                    this.nextComponent();
                } else {
                    this.showNotification({
                        'title': this.__('errors.url_exists_title'),
                        'message': this.__('errors.url_exists_message'),
                        'status': 'danger'
                    });
                }
            }, () => {
                this.showNotification({
                    'title': this.__('errors.url_exists_title'),
                    'message': this.__('errors.url_exists_message'),
                    'status': 'danger'
                });
            });
        },
        quantityDidUpdate(numLicenses, showContact, nextComponent = false) {
            if(parseInt(numLicenses) > this.maxLicenseQuantity && showContact){
                this.showContactFlow = true;
            } else {
                this.numLicenses = numLicenses;
            }

            if(nextComponent){
                this.nextComponent();
            }
        },
        netPriceDidUpdate(data){
            this.invoice.invoiceAmount = data
        },
        applyPromoCode(code, form){
            axios.post(window.s.API.promotionCodeCheckAPI, {
                'promoCode' : code,
            }).then(response => {
                const responseData = response.data;
                if(responseData.success){
                    this.freeLicenses = responseData.freeLicenses;
                    this.invoice.referralName = responseData.referralName;
                    this.invoice.promotionCode = code;
                    this.invoice.referral = responseData.referral;
                    this.creditcardReferrals = responseData.creditcardReferrals;
                    this.invoice.discountPercentage = responseData.discountRate * 100;
                    this.invoice.promotionCodeID = responseData.promotionCodeID;
                    this.setCookies();
                } else {
                    form.setErrors({ 'promotion_code': responseData.error });
                }
            }, (error) => {
                console.log(error);
            });
        },
        processPayment() {
            return axios.post(window.s.API.processPayment, {
                'purchase': {
                    numLicenses:this.numLicenses,
                    freeLicenses:this.freeLicenses,
                    businessDetails: this.businessDetails,
                    url: this.url,
                    billingDetails: this.billingDetails,
                    cardInformation: this.cardInformation,
                    invoice: this.invoice,
                },
            }).then((response) => {
                if(response.data.success){
                    this.sendPageViewHit(7, "Complete");
                    this.removePaymentCookies();
                    this.showNotification({
                        'title': this.__('purchase.payment_success_title'),
                        'message': this.__('purchase.payment_success_message'),
                        'status': 'success'
                    });
                    let venngoCookies = Cookies.get('venngo_cookies');
                    if (venngoCookies) {
                        venngoCookies = JSON.parse(venngoCookies);
                    } else {
                        venngoCookies = {}; 
                    }
                    venngoCookies.userId = response.data.userId;
                    Cookies.set('venngo_cookies', JSON.stringify(venngoCookies), { expires: 1/24 });
                    // Cookies.set('invoiceId',response.data.invoiceId, { expires: 1/24 });
                    navigateToUrl('/payment/confirmation');
                } else {
                    this.showNotification({
                        'title': this.__('errors.invalid_card_title'),
                        'message': response.data.error,
                        'status': 'danger'
                    });
                }
            }).catch((error) => {
                this.showNotification({
                    'title': this.__('errors.error_occured_title'),
                    'message': this.__('errors.error_occured_message'),
                    'status': 'danger'
                });
                console.log(error);
            }).then(() => { Swal.close() });
        },
        updatePurchaseFlowIndicator(indicator?) {
            const currentIndicatorIndex = indicator || this.currentComponentIndex;

            for (let index = 0; index < purchaseFlow.length; index++) {
                this.setPurchaseFlowIndicator(index, INDICATOR_DISTANCE_OPACITY[ Math.abs(currentIndicatorIndex - index) ] );
            }
        },
        setPurchaseFlowIndicator(indicatorIndex, opacity) {
            const element = this.$refs[`purchase__flow-indicator-${indicatorIndex}`];

            if(element) {
                (<HTMLElement>element).style.opacity = opacity;
            }
        },
        sendPageViewHit(viewIndex, viewLabel) {
            gtag('event', 'PurchaseFlowView', {
                'event_category': this.invoice.promotionCode && this.invoice.promotionCode.length > 0 ? `WorkperksSMB-${this.invoice.promotionCode}` : 'WorkperkSMB',
                'event_label': viewLabel,
                'value': viewIndex
            });
        },
        previousComponent() {
            this.componentWillAnimate();
            if(this.currentComponentIndex === this.lastComponentIndex || this.currentComponentIndex < this.lastComponentIndex){
                this.currentComponentIndex--;
            }
            else{
                this.currentComponentIndex = this.lastComponentIndex;
            }
            window.v._componentAnimationTimer = setTimeout( () => {
                this.componentDidAnimate();
            }, 500);
        },
        setCookies() {
            if(this.numLicenses <= this.maxLicenseQuantity){
                const smbCookies = {
                    numLicenses           : this.numLicenses,
                    businessDetails       : this.businessDetails,
                    url                   : this.url,
                    billingDetails        : this.billingDetails,
                    cardInformation       : this.cardInformation,
                    invoice               : this.invoice,
                    lastComponentIndex    : this.lastComponentIndex,
                    currentComponentIndex : this.currentComponentIndex,
                    creditcardReferrals   : this.creditcardReferrals,
                }
                let venngoCookies = Cookies.get('venngo_cookies');
                if (venngoCookies) {
                    venngoCookies = JSON.parse(venngoCookies);
                } else {
                    venngoCookies = {}; 
                }
                venngoCookies.smbCookies = smbCookies;
                Cookies.set('venngo_cookies', JSON.stringify(venngoCookies), { expires: 1/24 });
            }
        },
    },
    components: {
        LicenseQuantity,
        BusinessDetails,
        BusinessUrl,
        BillingDetails,
        Terms,
        CardInformation,
        DetailVerification,
        Invoice,
        Contact,
        Notification,
    }
});

app.use(VueExtensions);
app.mount('#app');